/**
 * Kite: Discount & Free Gift's own text, fixed where Kite does not already show it right (Ali, 12-13 Oct 2026).
 *
 * Independent, narrowly-scoped fixes, each a no-op if the text it looks for is not exactly there — nothing here
 * ever hides, removes, or guesses at a value; the discount itself is never touched.
 *
 *  1. CA — the order-value progress bar (product page, cart drawer, cart page; one widget, same markup in all
 *     three: ".gfgCCDProgressBarSegmentTitle") has no Catalan from Kite (their support confirmed it) and shows
 *     English. Replaced with Catalan, a space before "%" and "amount €" (single space) like the rest of the site.
 *     The percentage (kept in Kite's own highlighted span, never touched) and the € amount are read from Kite's
 *     markup each time.
 *  2. ES — the same progress bar, reformatted to "amount €" (Kite's own markup mixes "€100" and "€ 100", both
 *     with a double space before the €). The highest tier (15 %) also wrongly says "inferiores a 100 €" (the
 *     lowest tier's own direction and amount) instead of "superiores a 1.000 €" — found on this store, 12 Oct
 *     2026; Kite's "Widget settings" page could not be opened to check if it is editable there (Chrome does not
 *     take clicks inside this app's embedded frame — the same limitation as Track123/Shopify Messaging). Never a
 *     general "inferiores is wrong" rule — the lowest tier is correctly "inferiores" — the direction/amount
 *     override only ever applies to the LAST segment, and only when it reads exactly the known-wrong shape (Ali
 *     confirmed the right amount, 12 Oct 2026).
 *  3. CA and ES — two more places Kite renders text that needs fixing, neither of them the progress bar: the
 *     shipping-progress banner ("Envío gratis…" / "Se aplicará en el pago…") and, on CA only, the applied
 *     discount's own label in the cart summary (".cart-discounts__label" — a theme class, not Kite's; it is
 *     Shopify's own rendering of the discount's title, which Kite set in Spanish). On CA the banner is
 *     translated; on ES, Kite's own two imperative sentences are in the tú form ("Completa…", "Añade…") where
 *     the storefront is usted throughout — corrected to "Complete…" / "Añada…", nothing else changed.
 *
 *  4. EN (14 Oct 2026, Ali): Kite's own English bar reads "5% Off on Orders below €100" / "Off on Order more than";
 *     its shipping banner and the discount's title stay Spanish on /en. All three are put into English: "% off
 *     orders under/over €100", "Free shipping…", "7% off orders over €100" ("€1,000" with a comma).
 *  5. ES (14 Oct 2026): the discount's title in the cart reads "100 €" / "1.000 €" like the bar ("7 % de
 *     descuento en pedidos superiores a 100 €"). And the highest tier's known bug is now fixed per widget: a
 *     product page holds Kite's tier widget twice, and only the second one's last tier was being caught.
 *
 * Loaded on /ca, /es and /en (see layout/theme.liquid → snippets/scripts.liquid); which fixes run on which locale is
 * decided below, from request.locale.iso_code (passed in as window.__kiteLocale).
 */
(function () {
  "use strict";

  var locale = window.__kiteLocale || "";
  var SEGMENT_SELECTOR = ".gfgCCDProgressBarSegmentTitle";

  function formatThousands(digits) {
    // "1000" -> "1.000" (Catalan/Spanish thousands separator) — the amount itself is never changed
    return digits.replace(/\.|,/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }

  function formatThousandsEn(digits) {
    // "1000" -> "1,000" (English)
    return digits.replace(/\.|,/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  // Kite's two shapes of a tier: its English ("% Off on Orders below €100", "% Off on Order more than € 1000") and,
  // on /es, its Spanish ("% en pedidos inferiores a €100"). Read into {below: true|false, amount: "100"}.
  var EN_SEGMENT = /^%\s*Off\s+on\s+Orders?\s+(below|more than)\s*€\s*([\d.,]+)\s*$/i;
  var ES_SEGMENT = /^%\s*en pedidos (inferiores|superiores) a\s*€\s*([\d.,]+)\s*$/i;

  function readTier(text) {
    var m = text.match(EN_SEGMENT);
    if (m) return { below: m[1].toLowerCase() === "below", amount: m[2] };
    m = text.match(ES_SEGMENT);
    if (m) return { below: m[1].toLowerCase() === "inferiores", amount: m[2], spanish: true };
    return null;
  }

  function writeTier(tier) {
    if (locale === "ca") {
      var eur = formatThousands(tier.amount) + " €";
      return tier.below ? " % de descompte en comandes per sota de " + eur : " % de descompte en comandes a partir de " + eur;
    }
    if (locale === "en") {
      return "% off orders " + (tier.below ? "under" : "over") + " €" + formatThousandsEn(tier.amount);
    }
    return " % en pedidos " + (tier.below ? "inferiores" : "superiores") + " a " + formatThousands(tier.amount) + " €";
  }

  // ── 1, 2, 4: every tier of every widget, in the page's language ─────────────────────────────────────────
  function fixSegments() {
    var nodes = document.querySelectorAll(SEGMENT_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      var seg = nodes[i];
      // the last tier of ITS widget (a product page holds two widgets); each tier sits in its own wrapper, so the
      // widget is the tiers' common container, not the tier's parent
      var widget = seg.closest(".gfgCCDProgressBarSegmentTextContainer") || seg.parentNode;
      var siblings = widget ? widget.querySelectorAll(SEGMENT_SELECTOR) : [seg];
      var isLast = siblings[siblings.length - 1] === seg;
      for (var j = 0; j < seg.childNodes.length; j++) {
        var n = seg.childNodes[j];
        if (n.nodeType !== 3) continue;
        var tier = readTier(n.nodeValue);
        if (!tier) continue;
        if (tier.spanish && isLast && tier.below && formatThousands(tier.amount) === "100") {
          // the known bug: Kite's Spanish highest tier copies the lowest tier's direction and amount (Ali confirmed, 12 Oct 2026)
          tier = { below: false, amount: "1000" };
        }
        n.nodeValue = writeTier(tier);
      }
    }
  }

  // ── 3, 4, 5: the shipping banner and the discount's own title in the cart ─────────────────────────────────
  var SHIPPING = {
    ca: {
      "Envío gratis": "Enviament gratuït",
      "Completa estos pasos para desbloquearlo": "Completi aquests passos per desbloquejar-lo",
      "Envío gratis desbloqueado": "Enviament gratuït desbloquejat",
      "Se aplicará en el pago": "S'aplicarà en el pagament",
    },
    // ES: only Kite's tú-form imperative needs fixing to usted — everything else here is already right
    es: {
      "Completa estos pasos para desbloquearlo": "Complete estos pasos para desbloquearlo",
    },
    en: {
      "Envío gratis": "Free shipping",
      "Completa estos pasos para desbloquearlo": "Complete these steps to unlock it",
      "Envío gratis desbloqueado": "Free shipping unlocked",
      "Se aplicará en el pago": "Applied at checkout",
    },
  };
  var ADD_ITEMS = /^Añade artículos por valor de\s*€\s*([\d.,]+)\s*$/i;
  var DISCOUNT_LABEL = /^(\d+)\s*%\s*de descuento en pedidos (inferiores|superiores) a\s*([\d.,]+)\s*€\s*$/i;

  function addItems(amount) {
    if (locale === "ca") return "Afegeixi articles per valor de " + formatThousands(amount) + " €";
    if (locale === "en") return "Add items worth €" + formatThousandsEn(amount);
    return "Añada artículos por valor de " + formatThousands(amount) + " €";
  }

  function discountLabel(pct, below, amount) {
    if (locale === "ca") return pct + " % de descompte en comandes " + (below ? "per sota de " : "a partir de ") + formatThousands(amount) + " €";
    if (locale === "en") return pct + "% off orders " + (below ? "under" : "over") + " €" + formatThousandsEn(amount);
    return pct + " % de descuento en pedidos " + (below ? "inferiores" : "superiores") + " a " + formatThousands(amount) + " €";
  }

  function fixTextNode(textNode) {
    var v = textNode.nodeValue.trim();
    if (!v) return;
    var map = SHIPPING[locale] || {};
    if (Object.prototype.hasOwnProperty.call(map, v)) {
      textNode.nodeValue = textNode.nodeValue.replace(v, map[v]);
      return;
    }
    var m = v.match(ADD_ITEMS);
    if (m) { textNode.nodeValue = textNode.nodeValue.replace(v, addItems(m[1])); return; }
    m = v.match(DISCOUNT_LABEL);
    if (m) {
      var fixed = discountLabel(m[1], m[2].toLowerCase() === "inferiores", m[3]);
      if (fixed !== v) textNode.nodeValue = textNode.nodeValue.replace(v, fixed);
    }
  }

  // ── scan + observe ────────────────────────────────────────────────────────────────────────────────────────
  // Narrow on purpose: only Kite's own progress-bar/shipping-banner elements and the theme's one discount-label
  // class are ever looked at — never the whole page.
  var SHIPPING_SELECTOR = ".gfgShippingDiscountTopSectionContainer, .gfgDynamicProgressBarTextMessage, .cart-discounts__label";

  function walkTextNodes(root, fn) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    var n;
    while ((n = walker.nextNode())) fn(n);
  }

  function scan() {
    if (locale !== "ca" && locale !== "es" && locale !== "en") return;
    fixSegments();
    document.querySelectorAll(SHIPPING_SELECTOR).forEach(function (el) {
      walkTextNodes(el, fixTextNode);
    });
  }

  scan();
  // Kite redraws this widget on every cart change (quantity, add, the cart drawer opening). Some of its
  // re-renders replace the segment elements outright (childList); others patch the existing text node's value
  // in place (characterData) — both are observed, so a tier crossed after load is caught too. Re-scanning the
  // whole thing on any relevant mutation is simpler and just as safe as tracking each node: every fix here is
  // idempotent (once applied, the text no longer matches its own pattern, so re-running is a harmless no-op).
  var pending = false;
  function scheduleScan() {
    if (pending) return;
    pending = true;
    Promise.resolve().then(function () { pending = false; scan(); });
  }
  var observer = new MutationObserver(scheduleScan);
  observer.observe(document.documentElement, { childList: true, subtree: true, characterData: true });
})();
